import React from 'react';
import { LayoutSingleColumn } from '../../components';
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';
import css from './PrivacyPolicyPage.module.css';

const supportEmail = 'support@throttlexshare.com';

const Section = props => {
  const { title, children } = props;

  return (
    <section className={css.section}>
      <h2>{title}</h2>
      {children}
    </section>
  );
};

const PrivacyPolicyContent = props => {
  const { showTitle = true } = props || {};

  return (
    <div className={css.policyContent}>
      {showTitle ? (
        <>
          <h1>Privacy Policy</h1>
          <p className={css.updated}>Effective date: September 13, 2026</p>
        </>
      ) : null}

      <Section title="1. Introduction">
        <p>
          This Privacy Policy explains how ThrottleX Share collects, uses, stores, and
          shares information when users visit the marketplace, create an account, list
          rentals, request bookings, complete transactions, contact support, or otherwise
          use the platform.
        </p>
      </Section>

      <Section title="2. Information we collect">
        <p>
          We may collect account information such as name, email address, phone number,
          profile details, login information, and communication preferences.
        </p>
        <p>
          We may collect marketplace activity information such as listings, booking
          requests, rental dates, pickup and return details, messages, reviews, support
          requests, uploaded photos, vehicle information, owner rules, renter questions,
          and transaction history.
        </p>
        <p>
          Payment information is processed through third-party payment providers. ThrottleX
          does not store full credit card numbers. We may receive limited payment details,
          transaction status, payout status, deposit status, billing details, and related
          fraud-prevention information.
        </p>
        <p>
          We may also collect technical information such as device type, browser type,
          IP address, pages viewed, referral source, approximate location, cookie data,
          and usage activity.
        </p>
      </Section>

      <Section title="3. How we use information">
        <p>
          We use information to operate the marketplace, create and manage accounts,
          display listings, process bookings, support payments and deposits, help owners
          and renters communicate, provide customer support, improve the platform, detect
          fraud or misuse, enforce marketplace rules, and comply with legal obligations.
        </p>
      </Section>

      <Section title="4. Sharing information">
        <p>
          We may share information between renters and owners when necessary to support a
          booking, rental pickup, rental return, trip coordination, claim, dispute, or
          safety issue.
        </p>
        <p>
          We may share information with trusted service providers that help operate the
          platform, including hosting providers, payment processors, email providers,
          analytics tools, customer support tools, fraud-prevention services, insurers,
          claims administrators, and professional advisors.
        </p>
        <p>
          We may disclose information when reasonably necessary to comply with law, respond
          to legal requests, investigate fraud, enforce our terms, protect users, resolve
          disputes, or address safety and security concerns.
        </p>
      </Section>

      <Section title="5. Payments, deposits, and payouts">
        <p>
          Payments, security deposits, payment methods, and owner payouts may be handled by
          third-party payment providers. Those providers may collect and process payment,
          identity, tax, banking, fraud-prevention, and compliance information according to
          their own terms and privacy policies.
        </p>
      </Section>

      <Section title="6. Cookies and analytics">
        <p>
          ThrottleX may use cookies and similar technologies to keep users signed in,
          remember preferences, measure site performance, understand marketplace activity,
          improve pages, and protect against fraud or abuse.
        </p>
      </Section>

      <Section title="7. Data retention">
        <p>
          We keep information for as long as reasonably necessary to operate the marketplace,
          provide services, complete transactions, resolve disputes, enforce agreements,
          maintain records, prevent fraud, and comply with legal obligations.
        </p>
      </Section>

      <Section title="8. Security">
        <p>
          We use reasonable administrative, technical, and organizational safeguards to help
          protect user information. No online service can guarantee complete security, and
          users are responsible for keeping their login credentials private.
        </p>
      </Section>

      <Section title="9. User choices">
        <p>
          Users may update certain account information through their account settings.
          Users may also contact ThrottleX to ask questions about their information,
          request support, or ask about privacy-related choices available through the
          platform.
        </p>
      </Section>

      <Section title="10. Children">
        <p>
          ThrottleX is not intended for children. Users must meet the age and eligibility
          requirements required by the platform, rental owners, payment providers, and
          applicable law.
        </p>
      </Section>

      <Section title="11. Changes to this policy">
        <p>
          We may update this Privacy Policy from time to time. Updates will be posted on
          this page with a revised effective date.
        </p>
      </Section>

      <Section title="12. Contact">
        <p>
          For privacy questions or support requests, contact ThrottleX at{' '}
          <a href={`mailto:${supportEmail}`}>{supportEmail}</a>.
        </p>
      </Section>
    </div>
  );
};

const PrivacyPolicyPageComponent = () => {
  return (
    <LayoutSingleColumn topbar={<TopbarContainer />} footer={<FooterContainer />}>
      <main className={css.root}>
        <div className={css.hero}>
          <a href="/" className={css.backLink}>← Back to homepage</a>
          <p className={css.eyebrow}>Legal</p>
          <h1>Privacy Policy</h1>
          <p>
            How ThrottleX Share collects, uses, stores, and shares information across the
            rental marketplace.
          </p>
        </div>

        <div className={css.shell}>
          <PrivacyPolicyContent showTitle={false} />
        </div>
      </main>
    </LayoutSingleColumn>
  );
};

const PrivacyPolicyPage = PrivacyPolicyPageComponent;
const PRIVACY_POLICY_ASSET_NAME = 'privacy-policy';

export { PRIVACY_POLICY_ASSET_NAME, PrivacyPolicyPageComponent, PrivacyPolicyContent };

export default PrivacyPolicyPage;
